/**
 * Auth/API client — APE-226: migrated from @supabase/supabase-js to D1 + custom auth.
 *
 * Re-exports `apiClient` as `supabase` so all existing call sites continue to
 * compile without modification. The interface is intentionally minimal — only
 * the parts of the Supabase client actually used in this codebase are provided.
 *
 * Auth flow:
 *   1. initSession()      — create anonymous browsing session (cookie-based)
 *   2. auth_request_otp   — send OTP via Postmark
 *   3. auth_verify_otp    — verify OTP → backend issues HS256 JWT
 *   4. setAccessToken(jwt) — store JWT; all subsequent invoke() calls include it
 */

import { apiClient, setAccessToken, getAccessToken } from './apiClient';
import { initSession, linkSessionToUser, getSession, setFrontendSessionId } from './session';

export { setAccessToken, getAccessToken };

// ---------------------------------------------------------------------------
// Re-export as `supabase` for backward compat with existing call sites
// ---------------------------------------------------------------------------

export const supabase = apiClient;

// ---------------------------------------------------------------------------
// Session bootstrapping
// ---------------------------------------------------------------------------

initSession();

try {
  const current = getSession();
  if (current?.sessionId) {
    setFrontendSessionId(current.sessionId);
  }
} catch {
  /* Storage disabled */
}

// ---------------------------------------------------------------------------
// Link session to user after auth state change
// ---------------------------------------------------------------------------

apiClient.auth.onAuthStateChange((ev) => {
  if (ev === 'SIGNED_IN') {
    linkSessionToUser().catch(console.error);
  }
});

// ---------------------------------------------------------------------------
// Token auto-purge after 30 min page idle
// ---------------------------------------------------------------------------

function installIdlePurge() {
  let hiddenAt: number | null = null;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
    } else if (document.visibilityState === 'visible' && hiddenAt) {
      const delta = Date.now() - hiddenAt;
      hiddenAt = null;
      if (delta > 30 * 60_000) {
        apiClient.auth.signOut({ scope: 'local' }).catch(console.error);
      }
    }
  });
}

if (typeof document !== 'undefined' && document.visibilityState) {
  installIdlePurge();
}
